import { useState, useCallback } from "react";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { AIModalState } from "../enums/CompassAI";
import {
  AIFileLocation,
  AIPrefillEngagementRequest,
  AIPrefillEngagementResponse,
} from "../types/compassAITypes";
import { MESSAGES } from "../messages";

// =============================================================================
// CONSTANTS
// =============================================================================

const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25MB
const MAX_FILES = 5;
const MAX_PROMPT_LENGTH = 50000;
const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/csv",
  "text/plain",
];
const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".xlsx", ".csv", ".txt"];

// =============================================================================
// TYPES
// =============================================================================

export interface UploadedFile {
  file: File;
  fileKey: string | null;
  isUploading: boolean;
  isUploaded: boolean;
  error: string | null;
}

interface UseEngagementPrefillReturn {
  /** Current state of the prefill workflow */
  state: AIModalState;
  /** List of uploaded files with their status */
  uploadedFiles: UploadedFile[];
  /** Prefill result from AI */
  prefillResult: AIPrefillEngagementResponse | null;
  /** Error message if any */
  errorMessage: string | null;
  /** Add a file to upload queue and start upload */
  addFile: (file: File) => Promise<void>;
  /** Remove a file from the list */
  removeFile: (index: number) => void;
  /** Start AI prefill extraction */
  startPrefill: (
    companyId: string,
    prompt?: string,
  ) => Promise<AIPrefillEngagementResponse | null>;
  /** Reset to initial state */
  reset: () => void;
  /** Convenience flags */
  isUploading: boolean;
  isExtracting: boolean;
  isComplete: boolean;
  hasError: boolean;
  canAddMoreFiles: boolean;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Hook for AI-powered engagement form prefill.
 *
 * Manages the full workflow: upload files → extract fields → populate form.
 *
 * @example
 * ```tsx
 * const { addFile, startPrefill, prefillResult, uploadedFiles } = useEngagementPrefill();
 *
 * // Add files
 * await addFile(file);
 *
 * // Start prefill (requires company selection)
 * const result = await startPrefill(companyId, optionalPromptText);
 *
 * // Access extracted fields
 * console.log(result?.extracted_fields);
 * ```
 */
export function useEngagementPrefill(): UseEngagementPrefillReturn {
  const { addToast } = useToast();

  // State
  const [state, setState] = useState<AIModalState>(AIModalState.IDLE);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [prefillResult, setPrefillResult] =
    useState<AIPrefillEngagementResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // API hooks
  const { sendData: uploadFile } = usePost<AIFileLocation, { file: File }>();
  const { sendData: triggerPrefill } = usePost<
    AIPrefillEngagementResponse,
    AIPrefillEngagementRequest
  >();

  /**
   * Validate file before upload.
   */
  const validateFile = useCallback(
    (file: File): string | null => {
      // Check file count
      if (uploadedFiles.length >= MAX_FILES) {
        return `Maximum ${MAX_FILES} files allowed`;
      }

      // Check file type
      const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      const isValidType =
        ALLOWED_FILE_TYPES.includes(file.type) ||
        ALLOWED_EXTENSIONS.includes(extension);
      if (!isValidType) {
        return "Invalid file type. Allowed: PDF, DOCX, XLSX, CSV, TXT";
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        return "File size must be under 25MB";
      }

      return null;
    },
    [uploadedFiles.length],
  );

  /**
   * Add and upload a file.
   */
  const addFile = useCallback(
    async (file: File): Promise<void> => {
      const validationError = validateFile(file);
      if (validationError) {
        addToast(validationError, "error");
        return;
      }

      // Add file to list with uploading state
      const newFile: UploadedFile = {
        file,
        fileKey: null,
        isUploading: true,
        isUploaded: false,
        error: null,
      };

      setUploadedFiles((prev) => [...prev, newFile]);
      setState(AIModalState.UPLOADING);
      setErrorMessage(null);

      // Upload the file
      const result = await uploadFile(
        "UPLOAD_ENGAGEMENT_PDF",
        { file },
        {},
        true, // isFileUpload
      );

      if (result) {
        // Update file with success
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.file === file
              ? {
                  ...f,
                  fileKey: result.file,
                  isUploading: false,
                  isUploaded: true,
                }
              : f,
          ),
        );
        setState(AIModalState.UPLOADED);
      } else {
        // Update file with error
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.file === file
              ? { ...f, isUploading: false, error: "Upload failed" }
              : f,
          ),
        );
        addToast(MESSAGES.COMPASS_AI.ERRORS.UPLOAD_FAILED, "error");

        // Only set error state if no other files are successfully uploaded
        setUploadedFiles((prev) => {
          const hasSuccessful = prev.some(
            (f) => f.isUploaded && f.file !== file,
          );
          if (!hasSuccessful) {
            setState(AIModalState.ERROR);
          }
          return prev;
        });
      }
    },
    [uploadFile, validateFile, addToast],
  );

  /**
   * Remove a file from the list.
   */
  const removeFile = useCallback((index: number): void => {
    setUploadedFiles((prev) => {
      const newFiles = prev.filter((_, i) => i !== index);
      // Reset state if no files left
      if (newFiles.length === 0) {
        setState(AIModalState.IDLE);
      }
      return newFiles;
    });
  }, []);

  /**
   * Start AI prefill extraction.
   */
  const startPrefill = useCallback(
    async (
      companyId: string,
      prompt?: string,
    ): Promise<AIPrefillEngagementResponse | null> => {
      // Validate prompt length
      if (prompt && prompt.length > MAX_PROMPT_LENGTH) {
        addToast(
          MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.MAX_TEXT_LENGTH,
          "error",
        );
        return null;
      }

      // Get successfully uploaded file keys
      const fileKeys = uploadedFiles
        .filter((f) => f.isUploaded && f.fileKey)
        .map((f) => f.fileKey as string);

      // Need at least one file or prompt text
      if (fileKeys.length === 0 && !prompt?.trim()) {
        addToast("Please upload a file or enter text to extract from", "error");
        return null;
      }

      setState(AIModalState.EXTRACTING);
      setErrorMessage(null);

      const request: AIPrefillEngagementRequest = {
        company_id: companyId,
        ...(fileKeys.length > 0 && { file_keys: fileKeys }),
        ...(prompt?.trim() && { prompt: prompt.trim() }),
      };

      const result = await triggerPrefill("AI_PREFILL_ENGAGEMENT", request);

      if (result) {
        setPrefillResult(result);

        // Check for warnings
        if (result.company_mismatch) {
          addToast(
            MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.COMPANY_MISMATCH_WARNING,
            "warning",
          );
        } else if (result.files_processed.some((f) => !f.relevant)) {
          addToast(
            MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.FILES_IRRELEVANT_WARNING,
            "warning",
          );
        }

        // Check if any fields were extracted
        if (result.fields_found.length === 0) {
          addToast(
            MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.NO_FIELDS_EXTRACTED,
            "warning",
          );
        }

        setState(AIModalState.SUCCESS);
        return result;
      } else {
        setState(AIModalState.ERROR);
        setErrorMessage(
          MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.DESCRIPTION.ERROR,
        );
        addToast(
          MESSAGES.COMPASS_AI.ENGAGEMENT_PREFILL.DESCRIPTION.ERROR,
          "error",
        );
        return null;
      }
    },
    [uploadedFiles, triggerPrefill, addToast],
  );

  /**
   * Reset to initial state.
   */
  const reset = useCallback((): void => {
    setState(AIModalState.IDLE);
    setUploadedFiles([]);
    setPrefillResult(null);
    setErrorMessage(null);
  }, []);

  // Check if any file is currently uploading
  const isUploading = uploadedFiles.some((f) => f.isUploading);

  return {
    state,
    uploadedFiles,
    prefillResult,
    errorMessage,
    addFile,
    removeFile,
    startPrefill,
    reset,
    // Convenience flags
    isUploading,
    isExtracting: state === AIModalState.EXTRACTING,
    isComplete: state === AIModalState.SUCCESS,
    hasError: state === AIModalState.ERROR,
    canAddMoreFiles: uploadedFiles.length < MAX_FILES,
  };
}
